import Link from 'next/link';
import { GripVertical, Plus } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { WorkspaceBadge } from '../../WorkspaceBadge';
import { IconButton } from '../../ui';
import { useWorkspaceDrag } from '../hooks/use-workspace-drag';
import { useWorkspaceRows } from '../hooks/use-workspace-rows';
// `dragCss`, not `drag`: the css-module guard scans for `<alias>.<class>` and the alias
// `drag` also matches `drag.module` inside this very path.
import dragCss from '../workspace-drag.module.css';
import styles from './MobileSidebar.module.css';

// The mobile drawer's Workspaces group: the section label with the design's "+"
// beside it, then one flat row per workspace. Rows come from useWorkspaceRows —
// the same model the wide rail's WorkspaceNav renders.
export function MobileWorkspaces() {
  const { t } = useTranslation();
  const { ready, rows, create } = useWorkspaceRows();
  // Drag a row's grip to reorder, in place — the same hook the wide rail uses. `touch-action: none` on
  // the grip alone is what keeps a finger elsewhere on the row scrolling the drawer.
  const dnd = useWorkspaceDrag(rows);
  if (!ready) return null;

  return (
    <section className={`${styles.group} ${styles.groupWorkspaces}`}>
      <header className={styles.sectionHeader}>
        <span className={styles.sectionLabel}>{t('sidebar.workspaces')}</span>
        {/* Creating a workspace is an ask to Maestro, so the + is a link, not a form.
            A 30px sunken-surface tile — Figma node 7593-24450 fills it with
            Surface/Sunken + a Neutral/50 glyph (not the ghost search/bell). */}
        <IconButton
          href={create.route}
          linkAs={create.path}
          onClick={create.onClick}
          aria-label={create.label}
          size="small"
          className={styles.addWorkspace}
        >
          <Plus size={16} aria-hidden />
        </IconButton>
      </header>
      {dnd.rows.map((row) => (
        <Link
          key={row.ws.slug}
          href={row.href}
          as={row.as}
          onClick={row.onClick}
          {...dnd.rowProps()}
          aria-current={row.active ? 'page' : undefined}
          title={row.ws.name}
          className={`${styles.row} ${dragCss.row} ${dnd.isDragging(row.ws.id) ? dragCss.lifted : ''}`}
          style={dnd.liftStyle(row.ws.id)}
        >
          {/* A lone space has nothing to reorder against, so it gets no handle at all. */}
          {dnd.canReorder && (
            <span
              className={dragCss.grip}
              role="button"
              tabIndex={0}
              aria-label={t('workspace.reorder')}
              {...dnd.gripProps(row.ws.id)}
            >
              <GripVertical size={14} aria-hidden />
            </span>
          )}
          <span aria-hidden className={styles.rowIcon}>
            <WorkspaceBadge ws={row.ws} size={20} />
          </span>
          <span className={styles.rowLabel}>{row.ws.name}</span>
        </Link>
      ))}
    </section>
  );
}
